import { useState } from "react";
import ScreenA from "./ScreenA";

const API = "https://api.semiotix.ai";

export default function App() {
  const [query,      setQuery]      = useState("");
  const [submitted,  setSubmitted]  = useState("");
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [selection,  setSelection]  = useState([]);
  const [showMatrix, setShowMatrix] = useState(false);

  async function handleSearch(e) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setResults([]);
    setShowMatrix(false);
    setSelection([]);

    try {
      const r = await fetch(`${API}/search`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ query, num: 20 }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || "Search failed");
      }
      const data = await r.json();
      setResults(data);
      setSubmitted(query);
      setShowMatrix(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.logo}>semiotix<span style={styles.dot}>.ai</span></div>
        <p style={styles.tagline}>Document Intelligence Grounded In Semiotic Patterns</p>
      </header>

      <main style={styles.main}>
        <form onSubmit={handleSearch} style={styles.form}>
          <input
            style={styles.input}
            type="text"
            placeholder="Search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
          />
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? "Searching…" : "Search"}
          </button>
        </form>

        {error && <div style={styles.error}>{error}</div>}

        {results.length > 0 && (
          <div style={styles.columns}>

            {/* Results list */}
            <div style={styles.resultsList}>
              <div style={styles.resultsHeader}>
                {results.length} results
                {selection.length > 0 && (
                  <span style={{color:"#f0c060"}}>
                    {" · "}{selection.join(", ")}
                  </span>
                )}
              </div>
              {results.map((r, i) => (
                <div key={r.doc_id} style={styles.card}>
                  <div style={styles.cardRank}>{i + 1}</div>
                  <div style={styles.cardBody}>
                    <a href={r.url} target="_blank" rel="noreferrer" style={styles.cardTitle}>
                      {r.title}
                    </a>
                    <div style={styles.cardUrl}>{r.url}</div>
                    <div style={styles.cardSnippet}>{r.snippet}</div>
                    <div style={styles.cardMeta}>
                      {r.indexed
                        ? <span style={styles.indexed}>✓ indexed</span>
                        : <span style={styles.indexing}>⟳ indexing…</span>
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Screen A */}
            <div style={styles.matrixPanel}>
              {showMatrix
                ? <ScreenA query={submitted} onSelection={setSelection} />
                : (
                  <div style={styles.matrixWaiting}>
                    <div style={styles.ldot} />
                    <div style={styles.ldot} />
                    <div style={styles.ldot} />
                    <span>indexing documents…</span>
                  </div>
                )
              }
            </div>

          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  app: {
    minHeight: "100vh",
    background: "#0d0d0f",
    color: "#e8e4dc",
    fontFamily: "'DM Mono', 'Courier New', monospace",
    fontSize: 12,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "32px 40px 20px",
    borderBottom: "1px solid #2a2a35",
    flexShrink: 0,
  },
  logo: {
    fontSize: 28,
    fontWeight: 300,
    fontFamily: "Georgia, serif",
  },
  dot: { color: "#f0c060" },
  tagline: {
    color: "#6b6878",
    fontSize: 11,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    marginTop: 6,
  },
  main: {
    flex: 1,
    padding: "32px 40px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  form: {
    display: "flex",
    gap: 10,
    maxWidth: 600,
  },
  input: {
    flex: 1,
    background: "#141418",
    border: "1px solid #2a2a35",
    borderRadius: 3,
    color: "#e8e4dc",
    fontFamily: "inherit",
    fontSize: 13,
    padding: "10px 14px",
    outline: "none",
  },
  button: {
    background: "#f0c060",
    border: "none",
    borderRadius: 3,
    color: "#0d0d0f",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.1em",
    padding: "10px 20px",
    textTransform: "uppercase",
  },
  error: {
    background: "#2a1515",
    border: "1px solid #c9445a",
    borderRadius: 3,
    color: "#c9445a",
    fontSize: 12,
    padding: "10px 14px",
    maxWidth: 600,
  },
  columns: {
    display: "flex",
    gap: 24,
    flex: 1,
    alignItems: "flex-start",
  },
  resultsList: {
    width: 320,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  resultsHeader: {
    color: "#6b6878",
    fontSize: 10,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    paddingBottom: 8,
    borderBottom: "1px solid #2a2a35",
    marginBottom: 4,
  },
  card: {
    background: "#141418",
    border: "1px solid #2a2a35",
    borderRadius: 3,
    display: "flex",
    gap: 12,
    padding: "10px 12px",
  },
  cardRank: {
    color: "#3a3a45",
    fontSize: 10,
    minWidth: 16,
    paddingTop: 2,
  },
  cardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  cardTitle: {
    color: "#e8e4dc",
    fontSize: 12,
    fontFamily: "Georgia, serif",
    textDecoration: "none",
    lineHeight: 1.4,
  },
  cardUrl: {
    color: "#4a9b6f",
    fontSize: 10,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardSnippet: {
    color: "#9a96a0",
    fontSize: 11,
    lineHeight: 1.5,
  },
  cardMeta: { marginTop: 2 },
  indexed: {
    color: "#4a9b6f",
    fontSize: 10,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
  },
  indexing: {
    color: "#6b6878",
    fontSize: 10,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
  },
  matrixPanel: {
    flex: 1,
    background: "#0d0d0f",
    border: "1px solid #2a2a35",
    borderRadius: 3,
    minHeight: 500,
    overflow: "hidden",
  },
  matrixWaiting: {
    height: "100%",
    minHeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    color: "#3a3a45",
    fontSize: 10,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
  },
  ldot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#3a3a45",
  },
};
